import type { Alert, Camera, DashboardSummary, VisionEvent } from '@playplate/shared';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { CameraStatusPill, EmptyState, ErrorState, Loading, Panel, SeverityBadge, StatCard } from '../components/ui';
import { useCameraHealthFeed, useRealtimeSync } from '../hooks/useRealtime';
import { api } from '../lib/api';
import { formatHourBand, formatNumber, formatRelative, humanEventType } from '../lib/format';

/**
 * The Command Center.
 *
 * Occupancy comes from the WebSocket when it is connected and from the last
 * persisted record when it is not — and the tile says which. A number that
 * silently freezes is the failure mode that makes people stop trusting a wall
 * display.
 */
export function DashboardPage() {
  const { occupancy: liveOccupancy, status } = useRealtimeSync();

  const summaryQuery = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => api.get<DashboardSummary>('/dashboard/summary'),
    // Polling is the safety net for a dropped socket, not the primary path.
    refetchInterval: status === 'open' ? 60_000 : 15_000,
  });

  const camerasQuery = useQuery({
    queryKey: ['cameras', 'list'],
    queryFn: () => api.get<{ items: Camera[] }>('/cameras'),
    refetchInterval: 60_000,
  });

  const eventsQuery = useQuery({
    queryKey: ['events', 'recent'],
    queryFn: () => api.get<{ items: VisionEvent[] }>('/events?limit=8'),
  });

  const alertsQuery = useQuery({
    queryKey: ['alerts', 'active'],
    queryFn: () => api.get<{ items: Alert[] }>('/alerts/active'),
  });

  const healthFeed = useCameraHealthFeed(6);

  if (summaryQuery.isLoading) return <Loading label="Loading command center" />;
  if (summaryQuery.isError) {
    return (
      <ErrorState
        title="Cannot load the dashboard"
        message={(summaryQuery.error as Error).message}
        onRetry={() => void summaryQuery.refetch()}
      />
    );
  }

  const summary = summaryQuery.data!;
  const occupancyTotal = liveOccupancy?.totalOccupancy ?? summary.occupancy.total;
  const occupancyIsLive = liveOccupancy !== null && status === 'open';
  const cameras = camerasQuery.data?.items ?? [];

  const dining =
    liveOccupancy?.zones.filter((z) => z.kind === 'DINING').reduce((a, z) => a + z.occupancy, 0) ??
    summary.occupancy.dining;
  const gaming =
    liveOccupancy?.zones.filter((z) => z.kind === 'GAMING').reduce((a, z) => a + z.occupancy, 0) ??
    summary.occupancy.gaming;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Live Command Center</h1>
          <p className="mt-1 text-sm text-muted">
            {occupancyIsLive
              ? 'Occupancy updating live'
              : summary.occupancy.observedAt
                ? `Last occupancy reading ${formatRelative(summary.occupancy.observedAt)}`
                : 'No occupancy data yet'}
          </p>
        </div>
        {summary.alerts.active > 0 ? (
          <Link to="/alerts" className="btn-secondary">
            {summary.alerts.active} active alert{summary.alerts.active === 1 ? '' : 's'}
          </Link>
        ) : null}
      </header>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-6">
        <StatCard
          label="Cameras online"
          value={`${summary.cameras.online}/${summary.cameras.total}`}
          hint={
            summary.cameras.offline > 0
              ? `${summary.cameras.offline} offline`
              : summary.cameras.degraded > 0
                ? `${summary.cameras.degraded} degraded`
                : 'All healthy'
          }
          tone={summary.cameras.offline > 0 ? 'bad' : summary.cameras.degraded > 0 ? 'warn' : 'good'}
        />
        <StatCard
          label="Current occupancy"
          value={formatNumber(occupancyTotal)}
          hint={occupancyIsLive ? 'Live' : 'Last known'}
        />
        <StatCard label="Dining" value={formatNumber(dining)} hint="People in dining zones" />
        <StatCard label="Gaming" value={formatNumber(gaming)} hint="People in gaming zones" />
        <StatCard
          label="Active alerts"
          value={formatNumber(summary.alerts.active)}
          hint={summary.alerts.unacknowledged > 0 ? `${summary.alerts.unacknowledged} unacknowledged` : 'All acknowledged'}
          tone={summary.alerts.critical > 0 ? 'bad' : summary.alerts.high > 0 ? 'warn' : 'good'}
        />
        <StatCard
          label="Visitors today"
          value={formatNumber(summary.today.visitors)}
          hint={`Peak ${formatHourBand(summary.today.peakHour)}`}
          pending={summary.dataWarmup}
        />
      </div>

      {/* Quality of service at the billing counter */}
      <Panel title="Quality of service">
        <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2">
          <StatCard
            label="Cashier absence (today)"
            value={formatNumber(summary.qualityOfService.cashierAbsenceCount)}
            hint="Counter unattended >30s during opening hours"
            tone={summary.qualityOfService.cashierAbsenceCount > 0 ? 'warn' : 'good'}
            pending={summary.dataWarmup}
          />
          <StatCard
            label="Customers waiting >5 min (today)"
            value={formatNumber(summary.qualityOfService.customerWaitCount)}
            hint="At the billing counter"
            tone={summary.qualityOfService.customerWaitCount > 0 ? 'warn' : 'good'}
            pending={summary.dataWarmup}
          />
        </div>
      </Panel>

      {summary.dataWarmup ? (
        <p className="rounded-lg border border-line bg-panel px-4 py-3 text-sm text-muted">
          Analytics is still collecting data. Trends and daily figures become meaningful after the
          first full day of recording.
        </p>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-3">
        {/* Camera grid */}
        <div className="xl:col-span-2">
          <Panel
            title="Cameras"
            actions={
              <Link to="/cameras" className="text-xs font-semibold text-lime hover:underline">
                Manage
              </Link>
            }
          >
            {camerasQuery.isLoading ? (
              <Loading label="Loading cameras" />
            ) : cameras.length === 0 ? (
              <EmptyState
                title="No cameras configured"
                message="Add the store's RTSP cameras to begin monitoring."
                action={
                  <Link to="/cameras" className="btn-primary">
                    Add a camera
                  </Link>
                }
              />
            ) : (
              <div className="grid gap-3 p-3 sm:grid-cols-2">
                {cameras.map((camera) => (
                  <CameraTile key={camera.id} camera={camera} />
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          {/* Active alerts */}
          <Panel title="Active alerts">
            {alertsQuery.isLoading ? (
              <Loading />
            ) : (alertsQuery.data?.items.length ?? 0) === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted">Nothing needs attention.</p>
            ) : (
              <ul className="divide-y divide-line/50">
                {alertsQuery.data!.items.slice(0, 6).map((alert) => (
                  <li key={alert.id}>
                    <Link
                      to={`/alerts/${alert.id}`}
                      className="block px-4 py-3 transition-colors hover:bg-panel-raised"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">{alert.title}</p>
                          <p className="mt-0.5 text-xs text-muted">
                            {formatRelative(alert.lastSeenAt)}
                            {alert.status === 'ACKNOWLEDGED' ? ' · acknowledged' : ''}
                          </p>
                        </div>
                        <SeverityBadge severity={alert.severity} count={alert.occurrenceCount} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* Recent events */}
          <Panel
            title="Recent events"
            actions={
              <Link to="/events" className="text-xs font-semibold text-lime hover:underline">
                All events
              </Link>
            }
          >
            {eventsQuery.isLoading ? (
              <Loading />
            ) : (eventsQuery.data?.items.length ?? 0) === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted">No events in the last 24 hours.</p>
            ) : (
              <ul className="divide-y divide-line/50">
                {eventsQuery.data!.items.map((event) => (
                  <li key={event.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm">{humanEventType(event.eventType)}</p>
                      <p className="truncate text-xs text-muted">
                        {event.cameraName ?? 'System'}
                        {event.zoneName ? ` · ${event.zoneName}` : ''}
                      </p>
                    </div>
                    <span className="shrink-0 font-mono text-xs text-muted">
                      {formatRelative(event.occurredAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* Live health ticker — only appears once something has actually changed */}
          {healthFeed.length > 0 ? (
            <Panel title="Camera health changes">
              <ul className="divide-y divide-line/50">
                {healthFeed.map((update, index) => (
                  <li key={`${update.cameraId}-${update.changedAt}-${index}`} className="px-4 py-2.5">
                    <p className="text-sm">
                      <span className="font-semibold">{update.name}</span>{' '}
                      <span className="text-muted">
                        {update.previousStatus ? `${update.previousStatus} → ` : ''}
                        {update.status}
                      </span>
                    </p>
                    {update.reason ? <p className="text-xs text-muted">{update.reason}</p> : null}
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CameraTile({ camera }: { camera: Camera }) {
  const unhealthy = camera.status === 'OFFLINE' || camera.status === 'ERROR';

  return (
    <Link
      to={`/live?camera=${camera.id}`}
      className={clsx(
        'panel panel-hover block px-4 py-3',
        unhealthy && 'border-critical/40 bg-critical/5',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{camera.name}</p>
          <p className="truncate text-xs text-muted">{camera.location}</p>
        </div>
        <span className="label shrink-0">{camera.zoneKind.replace('_', '/')}</span>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <CameraStatusPill status={camera.status} fps={camera.currentFps} />
        <span className="font-mono text-xs text-muted">
          {camera.currentOccupancy === null ? '—' : `${camera.currentOccupancy} in zone`}
        </span>
      </div>

      {camera.statusReason ? (
        <p className="mt-2 truncate text-xs text-high" title={camera.statusReason}>
          {camera.statusReason}
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted">
          Last frame {formatRelative(camera.lastFrameAt)}
          {camera.recordingByNvr ? ' · NVR recording' : ''}
        </p>
      )}
    </Link>
  );
}
